// iOS Universal Links: when a build carrying the Associated
// Domains entitlement is installed, https links to these hosts open the app
// and arrive through Capacitor's `appUrlOpen` / `getLaunchUrl` — the same
// channel as the custom-scheme auth links, but with an https URL.
//
// This filter must stay in sync with the server's association file
// (src/server/api/appleAppSiteAssociation.ts): the AASA decides whether iOS
// opens the app at all; this decides where the app navigates. Both
// deliberately scope to round pages only.

export const UNIVERSAL_LINK_HOSTS = [
  "s-process.app",
  "staging.s-process.app",
] as const;

// Kept as a re-export for the iOS open handler; navigation itself is shared
// with browser surfaces that need the same Wouter + fragment event contract.
export { navigateInApp } from "@client/lib/navigation/navigateInApp";

/**
 * The in-app path (path + query + hash) for a universal-link URL, or null
 * for anything that isn't an https round link on one of our domains. The
 * hash rides along so passage deep links land and highlight exactly like
 * they do in a browser.
 */
export function parseUniversalLinkPath(url: string): string | null {
  let parsed: URL;

  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  if (parsed.protocol !== "https:") {
    return null;
  }

  if (!(UNIVERSAL_LINK_HOSTS as readonly string[]).includes(parsed.hostname)) {
    return null;
  }

  if (parsed.pathname !== "/round" && !parsed.pathname.startsWith("/round/")) {
    return null;
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * Marks a URL handled and reports whether the SAME URL was already handled
 * within the window. Capacitor can deliver one cold-start link through BOTH
 * `getLaunchUrl` and `appUrlOpen` (either order), and navigating twice
 * stacks a duplicate history entry and re-fires the landing events. A
 * persistent consumed-flag on both paths would be wrong the other way: a
 * user who later re-taps the same link must navigate again — so the dedupe
 * is a short window, not a permanent mark.
 */
export function createRecentUrlDeduper(
  windowMs = 3000,
): (url: string, now: number) => boolean {
  let last: { url: string; at: number } | null = null;

  return (url, now) => {
    if (last != null && last.url === url && now - last.at < windowMs) {
      return true;
    }

    last = { url, at: now };
    return false;
  };
}
